import React from "react";
import {
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Typography,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { TabProps } from "../../utility/interfaces/masterDataInterfaces";
import { ActiveLanguages } from "../../utility/interfaces/patientMasterDataInterface";
import CustomLanguage from "../../components/v2/custom-language-selector/CustomLanguageSelector";
import styles from "./MobileDrawer.module.scss";
import CustomButton from "../../components/v2/custom-button/CustomButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";
import HeaderIconMap from "./HeaderIconMap";
import { capitalizeFirstLetter } from "../../utility/functions/common-functions";
import CustomTooltip from "../../components/custom-tooltip/CustomTooltip";
interface MobileDrawerProps {
  open: boolean;
  userName: string;
  onClose: () => void;
  onProfileClick: () => void;
  supportedLanguages: ActiveLanguages[];
  selectedLanguage: string;
  langAnchorEl: HTMLElement | null;
  onLangMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  onLangMenuClose: () => void;
  onLangChange: (lang: ActiveLanguages) => void;
  tabs: TabProps[];
  onTabClick?: (event: React.SyntheticEvent, newValue: number) => void;
  onLogoutClick: () => void;
}

export const getIconComponent = (
  iconTitle: string
): React.ComponentType<any> | null => {
  const IconComponent = HeaderIconMap[capitalizeFirstLetter(iconTitle)];
  if (!IconComponent) {
    console.error(`Icon not found: ${iconTitle}`);
    return null;
  }
  return IconComponent;
};

const MobileDrawer: React.FC<MobileDrawerProps> = ({
  open,
  userName,
  onClose,
  onProfileClick,
  supportedLanguages,
  selectedLanguage,
  langAnchorEl,
  onLangMenuOpen,
  onLangMenuClose,
  onLangChange,
  tabs,
  onTabClick,
  onLogoutClick,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ className: styles["nav-drawer"] }}
      >
        <Box className={styles.drawerContent}>
          <Box className={styles.closeButton}>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Box px={2} mb={1}>
            <Typography variant="h2">
              {t("hello")} {userName}
            </Typography>
            <Box mt={2}>
              <Typography
                component="a"
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  onProfileClick();
                  onClose();
                }}
                className={styles.hyperlink}
              >
                {t("myProfile")}
              </Typography>
            </Box>
          </Box>

          <List>
            {tabs?.map((tab, index) => {
              const IconComponent = getIconComponent(tab.iconName);

              return (
                <>
                  <ListItem
                    key={tab.globalMenuDisplayName}
                    disablePadding
                    className={styles.listItem}
                  >
                    <ListItemButton>
                      {IconComponent && (
                        <ListItemIcon sx={{ minWidth: "auto", marginRight: 2 }}>
                          <IconComponent
                            width="16"
                            height="16"
                            fillColor={tab.isEnabled ? "#4e7e6e" : "#666666"}
                          />
                        </ListItemIcon>
                      )}
                      <ListItemText
                        primary={
                          <Box
                            className={`${styles.customTabItemText} ${
                              tab.isEnabled ? styles.active : styles.disabled
                            }`}
                          >
                            <Typography
                              className={styles.textWithIcon}
                              onClick={(event) => {
                                if (!tab.isEnabled) return; // Ensure only enabled tabs trigger navigation
                                onTabClick?.(event, tab.globalMenuId);
                                onClose(); // Close drawer after selecting a tab
                              }}
                            >
                              {tab.globalMenuDisplayName}
                            </Typography>
                            {!!tab.toolTipText && (
                              <CustomTooltip
                                title={tab.toolTipText}
                                enterTouchDelay={0}
                                leaveTouchDelay={3000}
                              >
                                <InfoOutlinedIcon className={styles.helpIcon} />
                              </CustomTooltip>
                            )}
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  {index < tabs.length - 1 && <Divider />}
                </>
              );
            })}
          </List>
          <Box className={styles.bottomSection}>
            <CustomButton
              sx={{ width: "100%" }}
              variant="outlined"
              onClick={onLogoutClick}
              text={t("logout")}
            />
            <CustomLanguage
              onLangMenuOpen={onLangMenuOpen}
              selectedLanguage={selectedLanguage}
              anchorEl={langAnchorEl}
              open={Boolean(langAnchorEl)}
              onClose={onLangMenuClose}
              supportedLanguages={supportedLanguages}
              onChangeLang={onLangChange}
            />
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default MobileDrawer;
